import React, { Component } from 'react'

import * as d3 from "d3"
import trans from "trans"

const {round, sqrt} = Math
import {
    Theme,
    Grid,
    GridCell,
    Button,
    Icon,
    InputGroup,
    Range,
    Message,
    css,
    FieldLabel,
} from "@bdfx/mergado-ui-kit"

import store from "store";

import { Gradient } from 'app/types/model-types';
import TransparentColor from "./transparent-color";
import { saveGradient } from "app/helpers/api";
import { createToast } from "actions";
import toastFactory from "app/helpers/toast_factory";
import {
    GENERAL_SUCCESS,
} from "template-edit/reducers";
import { GradientFillPreview, GradientSvg } from "./fields";

import { isJson } from 'app/helpers/other';
import { TextAlign } from 'shape-entities/types/textAlign';
import { initGradient } from 'settings/components/gradients';
import { hexToRgb, rgbToHex } from 'app/helpers/colors';
import { show } from '@bdfx/mergado-ui-kit/lib/components/Modal/common';
import { find, sortBy } from 'lodash'

interface Props {
    onSave?: (gradient: Gradient) => void
    updateGradients: Function
    gradient: Gradient
    gradients: Gradient[]
}

interface State {
    gradient: Gradient
    editIdx: number
    showPicker: boolean
}

export class GradientColorPicker extends Component<Props, State> {
    constructor(props) {
        super(props);

        const stopPoints = props.gradient?.stop_points && typeof props.gradient.stop_points === 'string' && isJson(props.gradient.stop_points) ?
            JSON.parse(props.gradient.stop_points) : props.gradient?.stop_points

        this.state = {
            gradient: {
                ...props.gradient,
                stop_points: stopPoints
            },
            editIdx: null,
            showPicker: false,
        };
    }

    componentDidMount() {
        this.bindEvents()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        const filteredStopPoints = this.state.gradient.stop_points.filter(c => c.color)
        if (filteredStopPoints.length !== this.state.gradient.stop_points.length) {
            this.setState({
                gradient: {
                    ...this.props.gradient,
                    stop_points: filteredStopPoints
                }
            })
            this.bindEvents()
        } else if (prevState && prevState.gradient.stop_points.length < this.state.gradient.stop_points.length && this.state.editIdx) {
            this.bindEvents()
        }
    }

    bindEvents = () => {
        const { gradient } = this.state
        let types = []

        if (gradient.stop_points && Array.isArray(gradient.stop_points)) {
            gradient.stop_points.forEach((stopPoint, key) => {
                const type = `stop-point-${key}`
                types.push(type)
                this.bindEventForType(type, key)
            })
        }

        types.push('first-point')
        types.push('second-point')

        this.bindEventForType('first-point')
        this.bindEventForType('second-point')
    }

    bindEventForType = (type: string, key: number = null) => {
        const el = document.getElementById(type)
        if (el) {
            d3.select(el)
                .call(
                    d3.drag()
                        .on("start", (event: MouseEvent) => this.dragStarted(event, type, key))
                        .on("drag", (event: MouseEvent) => this.dragged(event, type))
                        .on("end", (event: MouseEvent) => this.dragEnded(event, type))
                );
        }
    }

    handleSaveGradient = (saveNew: boolean) => {
        const { gradient } = this.state

        const updatedGradient = { ...gradient, id: saveNew ? null : gradient.id }

        saveGradient(updatedGradient)
            .then((response: any) => {
                this.setState({ gradient: response.data })
                this.props.updateGradients()
                store.dispatch(createToast(toastFactory(GENERAL_SUCCESS)))

                if (typeof this.props.onSave !== "undefined") {
                    this.props.onSave(response.data)
                }
            })
            .catch(error => {
                console.error('cannot save gradient', error)
            })
    }

    dragStarted = (event: MouseEvent, type: string, key: number) => {
        //console.log('drag started for', type, event.x, event.y)
        this.toggleColorPicker(key)
    }

    dragged = (event, type) => {
        const { gradient } = this.state
        const x = 100 * (event.x < 0 ? 0 : (event.x > 256 ? 256 : event.x)) / 256;
        const y = 100 * (event.y < 0 ? 0 : (event.y > 256 ? 256 : event.y)) / 256;

        switch (type) {
            case 'first-point':
                this.setState({ gradient: { ...gradient, x1: x, y1: y } })
                break
            case 'second-point':
                this.setState({ gradient: { ...gradient, x2: x, y2: y } })
                break
            default:
                const a = type.split('-')
                if (typeof a[2] !== "undefined") {
                    const editIdx = parseInt(`${a[2]}`)
                    let updatedStops = []
                    if (gradient.stop_points && Array.isArray(gradient.stop_points)) {
                        gradient.stop_points.forEach((stopPoint, key) => {
                            updatedStops.push({ ...stopPoint, offset: (key === editIdx) ? x : stopPoint.offset })
                        })
                        this.setState({ gradient: { ...gradient, stop_points: updatedStops } })
                    }
                }
                break
        }
    }

    dragEnded = (event, type) => {
        //console.log('drag ended for', type)
    }

    toggleColorPicker = (key: number) => {
        const { showPicker, editIdx } = this.state

        this.setState({
            editIdx: key,
            showPicker: (showPicker && key !== editIdx) || !showPicker,
        })
    }

    addNewColor = (e) => {
        const { gradient } = this.state
        const target = e.target as HTMLElement

        if (target.tagName === 'rect') {
            const box = target.getBoundingClientRect()
            let offset = e.clientX - box.left
            if (offset < 0) {
                offset = 0
            } else if (offset > 256) {
                offset = 256
            }
            offset = 100 * offset / 256

            const updatedStopPoints = sortBy([...gradient.stop_points, {
                offset: offset,
                color: '#aaaaaa'
            }], [(stopPoint) => {
                return stopPoint.offset
            }])

            let nearestFromTheLeft = null
            let nearestFromTheRight = null
            let currentPointKey = null

            updatedStopPoints.forEach((stopPoint, key) => {
                if (stopPoint.offset === offset) {
                    currentPointKey = key
                } else if (stopPoint.offset < offset && (!nearestFromTheLeft || stopPoint.offset > nearestFromTheLeft.offset)) {
                    nearestFromTheLeft = { ...stopPoint }
                } else if (stopPoint.offset > offset && (!nearestFromTheRight || stopPoint.offset < nearestFromTheRight.offset)) {
                    nearestFromTheRight = { ...stopPoint }
                }
            })

            if (nearestFromTheLeft && nearestFromTheRight && currentPointKey !== null) {
                const color1 = hexToRgb(nearestFromTheLeft.color)
                const color2 = hexToRgb(nearestFromTheRight.color)
                updatedStopPoints[currentPointKey].color = rgbToHex({
                    r: round((color1.r + color2.r) / 2),
                    g: round((color1.g + color2.g) / 2),
                    b: round((color1.b + color2.b) / 2),
                })
            }

            this.setState({
                gradient: {
                    ...gradient,
                    stop_points: updatedStopPoints
                },
                editIdx: currentPointKey,
                showPicker: true,
            })

            this.toggleColorPicker(currentPointKey)
        }
    }

    changeGradientColor = (color: string, idx: number) => {
        const { gradient } = this.state
        let updatedStopPoints = [...JSON.parse(JSON.stringify(gradient.stop_points))]
        updatedStopPoints[idx].color = color
        this.setState({
            gradient: {
                ...gradient,
                stop_points: updatedStopPoints
            },
            showPicker: false,
            editIdx: idx,
        })
    }

    deleteStopPoint = (idx: number) => {
        const { gradient, editIdx } = this.state

        if (gradient.stop_points.length > 2) {
            let updatedStopPoints = []
            gradient.stop_points.forEach((stopPoint, i) => {
                if (i !== idx) {
                    updatedStopPoints.push(stopPoint)
                }
            })
            this.setState({
                gradient: {
                    ...gradient,
                    stop_points: updatedStopPoints
                },
                showPicker: false,
                editIdx: editIdx !== idx ? editIdx : null,
            })
        }
    }

    render() {
        const { gradient, editIdx, showPicker } = this.state
        const { gradients } = this.props

        const addNew = true

        const d = "M-6.2,0.9c3.6-4,6.7-4.3,6.7-12.4c-0.2,7.9,3.1,8.8,6.5,12.4c3.5,3.8,2.9,9.6,0,12.3c-3.1,2.8-10.4,2.7-13.2,0C-9.6,9.9-9.4,4.4-6.2,0.9z"

        const currentGradient = find(gradients, (g: Gradient) => {
            return g.id === gradient.id
        })

        const isGradientDiff = gradient !== initGradient && (!currentGradient || JSON.stringify(currentGradient) !== JSON.stringify(gradient))

        if (!gradient.stop_points) {
            console.debug('No stop points defined!', gradient)
            return <div />
        }

        const x1 = 10 + 256 * gradient.x1 / 100
        const y1 = 256 * (gradient.y1 ?? 1) / 100

        const x2 = 10 + 256 * gradient.x2 / 100
        const y2 = 256 * (gradient.y2 ?? 1) / 100

        const b = (y1 - y2) / (x1 - x2)
        const sign = x1 > x2 ? 1 : -1

        const dx = sign * sqrt(20 * 20 / (b * b + 1))
        const dy = b * dx

        const vectorColor = '#efefef'

        //const canRemoveStopPoints = gradient.stop_points.length > 2

        const firstSampleGradientColor = gradient.stop_points[0]?.color ?? '#ffffff'
        const secondSampleGradientColor = gradient.stop_points[1]?.color ?? Theme.steelblue

        const sampleGradients: Gradient[] = [
            {
                type: 'linear',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                stop_points: [
                    { offset: 0, color: firstSampleGradientColor },
                    { offset: 100, color: secondSampleGradientColor },
                ]
            },
            {
                type: 'linear',
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 100,
                stop_points: [
                    { offset: 0, color: firstSampleGradientColor },
                    { offset: 100, color: secondSampleGradientColor },
                ]
            },
            {
                type: 'linear',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 100,
                stop_points: [
                    { offset: 0, color: firstSampleGradientColor },
                    { offset: 100, color: secondSampleGradientColor },
                ]
            },
            {
                type: 'radial',
                x1: 50,
                y1: 50,
                x2: 50,
                y2: 50,
                stop_points: [
                    { offset: 0, color: firstSampleGradientColor },
                    { offset: 100, color: secondSampleGradientColor },
                ]
            },
            {
                type: 'radial',
                x1: 50,
                y1: 50,
                x2: 50,
                y2: 50,
                stop_points: [
                    { offset: 0, color: secondSampleGradientColor },
                    { offset: 100, color: firstSampleGradientColor },
                ]
            },
            {
                type: 'linear',
                x1: 60,
                y1: 30,
                x2: 30,
                y2: 60,
                stop_points: [
                    { offset: 0, color: Theme.yellow },
                    { offset: 15, color: Theme.orange },
                    { offset: 100, color: '#cc0000' },
                ],
            }
        ]

        return (
            <Grid cols={'1fr 1fr'}>
                <GridCell col={'1/-1'} className={'hide-buttons'}>
                    <InputGroup
                        orientation="horizontal"
                        onChange={(value) => {
                            this.setState({
                                gradient: {
                                    ...gradient,
                                    type: value,
                                    x1: (value === 'linear' ? 0 : 50),
                                    y1: (value === 'linear' ? 0 : 50),
                                    x2: (value === 'linear' ? 100 : 50),
                                    y2: (value === 'linear' ? 0 : 50)
                                },
                            })
                        }}
                        value={gradient.type}
                        items={[
                            { value: 'linear', label: trans('gradient_type_linear') },
                            { value: 'radial', label: trans('gradient_type_radial') },
                        ]}
                        singleChoice={true}
                        style={{ width: '100%' }}
                    />
                </GridCell>

                <GridCell style={{ alignSelf: 'end' }}>
                    <svg id="gradient-picker-main" viewBox={'-10 0 300 300'} width="276" height="300" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" stroke={vectorColor} fill={vectorColor} />
                            </marker>
                        </defs>

                        <g transform={'translate(10,0)'}>
                            <GradientFillPreview gradient={gradient} idx={'edited'} />
                        </g>

                        <g>
                            <line x1={x1} y1={y1} x2={x2 + dx} y2={y2 + dy} stroke={vectorColor} stroke-width="1" marker-end="url(#arrowhead)" />
                        </g>

                        <g style={{ cursor: 'move' }} id={'first-point'}>
                            <circle
                                r={8}
                                cx={x1}
                                cy={y1}
                                fill={'#3a3a3a'}
                                strokeWidth={2}
                                stroke={Theme.grey}
                                className={'handles'}
                            />
                            <text
                                x={x1}
                                y={y1}
                                transform={`translate(${0},${0})`}
                                dominantBaseline={"central"}
                                textAnchor={"middle"}
                                stroke={'#ffffff'}
                                fontSize={10}>1
                            </text>
                        </g>

                        <g style={{ cursor: 'move' }} id={'second-point'}>
                            <circle
                                r={8}
                                cx={x2}
                                cy={y2}
                                fill={'#3a3a3a'}
                                strokeWidth={2}
                                stroke={Theme.grey}
                                className={'handles'}
                            />
                            <text
                                x={x2}
                                y={y2}
                                transform={`translate(${0},${0})`}
                                dominantBaseline={"central"}
                                textAnchor={"middle"}
                                stroke={'#ffffff'}
                                fontSize={10}>2
                            </text>
                        </g>

                        <rect x="10" y="260" width="256" height="40" fill={'transparent'} style={{ cursor: addNew ? 'copy' : 'remove' }} onClick={(e) => this.addNewColor(e)} />

                        <g transform={`translate(1,256)`}>
                            {gradient.stop_points && Array.isArray(gradient.stop_points) &&
                                gradient.stop_points.map((stopPoint, key) => <g key={`stop-point-${key}`}>
                                    <path
                                        id={`stop-point-${key}`}
                                        d={d}
                                        fill={stopPoint.color}
                                        transform={`translate(${10 + 256 * stopPoint.offset / 100}, 26)`}
                                        stroke="#000"
                                        strokeWidth={"1.5"}
                                        style={{ cursor: 'col-resize' }}
                                        /* onClick={(e) => this.toggleColorPicker(key)} */
                                    />
                                </g>
                                )}
                            <line x1="10" y1="10" x2="266" y2="10" strokeLinecap={"round"} strokeWidth={"1"} stroke="#3a3a3a" />
                            <circle r={2} cx={10} cy={10} fill={'#3a3a3a'} />
                            <circle r={2} cx={266} cy={10} fill={'#3a3a3a'} />
                            <line x1="10" y1="5" x2="10" y2="15" strokeWidth={"1"} stroke="#000" />
                            <line x1="266" y1="5" x2="266" y2="15" strokeWidth={"1"} stroke="#000" />
                        </g>
                    </svg>
                </GridCell>

                <GridCell className='stop-points' style={{ alignSelf: 'start !important', paddingTop: '20px', }}>
                    <FieldLabel>{trans('gradient_stop_points')}</FieldLabel>
                    <StopPointsGrid cols={'auto auto'} gap={'10px'}>
                        {gradient.stop_points.map((stopPoint, idx) => {
                            return <GridCell
                                key={`edit-color-${idx}-${editIdx}-${showPicker ? 1 : 0}-${stopPoint.color}`}
                                style={{ position: 'relative' }}
                                className={`${editIdx === idx ? '' : 'in-'}active`}
                                title={`stop point ${idx}`}>

                                <TransparentColor
                                    value={stopPoint.color}
                                    changeValue={(color) => this.changeGradientColor(color, idx)}
                                    hideGradients={true}
                                    disablePopover={true}
                                    doNotScrollIntoFocus={true}
                                    displayColorPicker={editIdx === idx && showPicker}
                                />

                                {/* {canRemoveStopPoints &&
                                    <span className={'clickable'} style={{ position: 'absolute', right: '1px', bottom: '-2px' }} onClick={(e) => this.deleteStopPoint(idx)}>
                                        <Icon type={'trash'} color={Theme.red} size={12} />
                                    </span>
                                } */}
                            </GridCell>
                        }
                        )}
                    </StopPointsGrid>

                    {gradient.type === 'radial' &&
                        <Range min={1} max={300} step={10} label={trans('radius')} value={gradient.radius ?? 50} onChange={(value) => this.setState({ gradient: { ...gradient, radius: value } })} />
                    }

                    <Range min={5} max={100} step={10} label={trans('opacity')} value={gradient.opacity ?? 100} onChange={(value) => this.setState({ gradient: { ...gradient, opacity: value } })} />

                    <Grid cols={'repeat(auto-fill, 38px)'} style={{' .muk-gridcell': {textAlign: 'center', alignSelf: 'center'} }}>  {/* display: 'inline-flex', */}
                        {sampleGradients.map((g, key) =>
                            <GridCell key={`gradient-${key}`} className={'clickable'} onClick={() => this.setState({ gradient: g })}>
                                <GradientSvg gradient={g} idx={`sample_gradient_${key}`} width={30} height={30} style={{paddingTop: '4px',  paddingLeft: '3px', scale: 1.2}} />
                            </GridCell>
                        )}
                    </Grid>
                </GridCell>

                {isGradientDiff &&
                    <GridCell col='1/-1'>
                        <StyledToast type={"warning"} closeable={false} icon={<Icon textFirst={true} type={'exclamation-triangle'} color={Theme.orange} size={28} />}>
                            <div>{trans('info_gradients_not_saved')}</div>

                            <StyledGrid>
                                {currentGradient &&
                                    <React.Fragment>
                                        <GridCell>
                                            <GradientSvg gradient={currentGradient} idx={`edited-${currentGradient.id}`} width={100} height={100} style={{ paddingTop: '15px', paddingLeft: '10px' }} />
                                        </GridCell>
                                        <GridCell>
                                            <Icon size={22} type={'long-arrow-right'} color={Theme.steelblue} />
                                        </GridCell>
                                        <GridCell>
                                            <GradientSvg gradient={gradient} idx={`edited-new-${gradient.id}`} width={100} height={100} style={{ paddingTop: '15px', paddingLeft: '10px' }} />
                                        </GridCell>
                                        <GridCell className={'clickable'} title={trans('history_undo')} onClick={() => this.setState({ gradient: this.props.gradient?.id ? this.props.gradient : initGradient })} style={{paddingRight: '10px'}}>
                                            <Icon type={'undo-light'} size={22} color={Theme.steelblue} />
                                        </GridCell>
                                    </React.Fragment>
                                }
                                <GridCell col={currentGradient ? undefined : '1/-1'}>
                                    <StyledButton
                                        icon={<Icon type="floppy-o">{trans(gradient.id ? 'save_and_apply' : 'save_as_new_gradient')}</Icon>}
                                        onClick={() => this.handleSaveGradient(false)}
                                        color={Theme.green}
                                        disabled={!isGradientDiff}
                                        actions={gradient.id ? [
                                            { label: trans('save_as_new_gradient'), onClick: () => this.handleSaveGradient(true), disabled: !isGradientDiff }
                                        ] : undefined}
                                    />
                                </GridCell>
                            </StyledGrid>
                        </StyledToast>
                    </GridCell>
                }
            </Grid>
        )
    }
}

const StyledGrid = css(Grid)({
    gridTemplateColumns: '1fr max-content 1fr max-content auto',
    marginTop: '10px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
    }
})

const StyledButton = css(Button)({
    alignSelf: 'center',
})

const StyledToast = css(Message)({
    textAlign: 'center',
    alignSelf: 'center',
})

const StopPointsGrid = css(Grid)({
    paddingBottom: '20px',
    ' .active': {
        border: `3px solid ${Theme.blue}`
    },
    ' .in-active': {
        border: '3px solid transparent'
    }
})

export default GradientColorPicker